import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

/**
 * Entitlement badge (Entitlement Sprint 2, step 8 — spec E2/E3).
 * Fetches GET /api/me/entitlements?unitId=<active> whenever the active owned
 * unit changes and shows a small pill with the effective plan for that context.
 */

interface UnitEntitlement {
  condoPaid: boolean;
  unitPaid: boolean;
  homeUltra: boolean;
  plan: string;
  source: 'condo' | 'unit' | 'home_ultra' | 'home_unlimited' | 'free';
  coverageCapped?: boolean;
  cappedByPlan?: string;
}

const STYLES: Record<string, { text: string; className: string }> = {
  condo: {
    text: 'Condo',
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
  home_ultra: {
    text: 'HomeUltra',
    className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  },
  home_unlimited: {
    text: 'HomeUnlimited',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  home_plus: {
    text: 'Home+',
    className: 'bg-[var(--v-teal-50,#e6f7f5)] text-[var(--v-teal-700,#0f766e)]',
  },
  free: {
    text: 'Gratis',
    className: 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)]',
  },
};

export function EntitlementBadge({ unitId }: { unitId?: string }) {
  const [entitlement, setEntitlement] = useState<UnitEntitlement | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!unitId) {
      setEntitlement(null);
      return;
    }
    api
      .get('/me/entitlements', { params: { unitId } })
      .then((res) => {
        if (!cancelled) setEntitlement(res.data?.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setEntitlement(null);
      });
    return () => {
      cancelled = true;
    };
  }, [unitId]);

  if (!unitId || !entitlement) return null;

  const key = entitlement.source === 'condo'
    ? 'condo'
    : entitlement.source === 'home_unlimited'
      ? 'home_unlimited'
      : entitlement.homeUltra || entitlement.source === 'home_ultra'
        ? 'home_ultra'
        : entitlement.unitPaid
          ? 'home_plus'
          : 'free';
  const style = STYLES[key] || STYLES.free;

  const capped = entitlement.coverageCapped === true && entitlement.cappedByPlan === 'home_ultra';
  return (
    <span className="inline-flex flex-col items-start gap-1">
      <span
        title={`Plan de la unidad activa: ${style.text}`}
        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${style.className}`}
      >
        {style.text}
      </span>
      {capped && (
        <span
          title="Esta unidad queda fuera del límite de 9 unidades de HomeUltra."
          className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 dark:bg-amber-900/30 dark:border-amber-700 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:text-amber-300"
        >
          HomeUltra cubre hasta 9 unidades —&nbsp;
          <Link to="/billing" className="underline hover:text-amber-800 dark:hover:text-amber-200">
            pasa a HomeUnlimited
          </Link>
        </span>
      )}
    </span>
  );
}

export default EntitlementBadge;
